import { create } from 'zustand';
import { SelectListItem } from 'apis/chatbot/Chatbot';

/**
 * 챗봇 대화 상태 — **화면 지역 상태가 아니라 전역이다.**
 *
 * 패널이 각 화면 안에서 렌더되던 동안에는 페이지가 언마운트될 때마다(MDI 탭 전환 등)
 * 대화와 세션 ID가 함께 사라졌다. 대화 맥락은 화면 수명과 무관해야 하므로 여기로 옮겼다.
 * 패널 자체도 App에서 하나만 렌더한다 — 열림 여부(open)와 화면 맥락(pageContext)도
 * 그래서 이 스토어에 있다.
 *
 * 영속화(sessionStorage)는 넣지 않았다. AI 서버 세션이 만료되면 "복원된 대화 + 새 세션"이
 * 되어 맥락이 어긋나기 때문이다. 필요해지면 persist 미들웨어를 검토한다.
 */

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  selectItems?: SelectListItem[];
}

export const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    '안녕하세요! 이런 것들을 물어보실 수 있어요.\n\n💡 "HD1 Assembly 공정의 CDA 사용량은?"\n💡 "확정완료된 UT Matrix는 공장별로 몇 개야?"',
  timestamp: new Date(),
};

/**
 * 진행 중인 스트림 핸들 — 상태가 아니라 참조라 스토어 밖에 둔다.
 * 패널이 언마운트돼도 중단할 수 있어야 하므로 모듈 스코프에 보관한다.
 */
let abortController: AbortController | null = null;

export const setChatAbortController = (controller: AbortController | null) => {
  abortController = controller;
};

export const abortChatStream = () => abortController?.abort();

interface ChatbotState {
  /** 패널 열림 여부 */
  open: boolean;
  /** 세션 발급 시 넘길 화면 맥락 (예: 'ut-matrix') */
  pageContext?: string;
  messages: ChatMessage[];
  sessionId: string | null;
  isLoading: boolean;
  /** AI가 요구한 후속 입력 (예: 'need_matrix') */
  pendingAction: string | null;

  openPanel: (pageContext?: string) => void;
  closePanel: () => void;
  setMessages: (updater: (prev: ChatMessage[]) => ChatMessage[]) => void;
  resetMessages: () => void;
  setSessionId: (sessionId: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setPendingAction: (pendingAction: string | null) => void;
}

export const useChatbotStore = create<ChatbotState>((set) => ({
  open: false,
  pageContext: undefined,
  messages: [WELCOME_MESSAGE],
  sessionId: null,
  isLoading: false,
  pendingAction: null,

  openPanel: (pageContext) => set({ open: true, pageContext }),
  closePanel: () => set({ open: false }),
  setMessages: (updater) => set((prev) => ({ messages: updater(prev.messages) })),
  resetMessages: () => set({ messages: [{ ...WELCOME_MESSAGE, timestamp: new Date() }] }),
  setSessionId: (sessionId) => set({ sessionId }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setPendingAction: (pendingAction) => set({ pendingAction }),
}));
